import React, { useState, useMemo } from 'react';
import { Card, Table, Button, Badge } from 'react-bootstrap';
import { FaCheck, FaAngleUp, FaAngleDown } from 'react-icons/fa';

const formatarMoeda = (valor) =>
  Number(valor).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const formatarDataHora = (data) => {
  if (!data) return '-';
  const d = new Date(data);
  return d.toLocaleDateString('pt-BR') + ' ' +
    d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
};

const colunas = [
  { chave: 'ge', label: 'GE', align: 'center', width: '70px' },
  { chave: 'codigo_conta', label: 'Conta', align: 'center', width: '100px' },
  { chave: 'nome', label: 'Moeda', align: 'center', width: '120px' },
  { chave: 'saldo', label: 'Saldo', align: 'center', width: '140px' },
  { chave: 'data', label: 'Ultima Conferencia', align: 'center', width: '180px' },
];

const MobileCard = ({ item, podeConferir, onConferir, isSelecionada }) => (
  <div
    className="card shadow-sm mb-2"
    style={{
      borderRadius: '8px',
      border: isSelecionada ? '2px solid #28a745' : '1.5px solid #dee2e6',
      background: isSelecionada ? '#f0fff0' : '#f8f9fa',
    }}
  >
    <div className="card-body p-2">
      <div className="d-flex flex-wrap" style={{ gap: '0.25rem 1rem' }}>
        <div>
          <small className="text-secondary d-block">GE</small>
          <strong>{item.ge}</strong>
        </div>
        <div>
          <small className="text-secondary d-block">Conta</small>
          <strong>{item.codigo_conta}</strong>
        </div>
        <div>
          <small className="text-secondary d-block">Moeda</small>
          <span>{item.nome}</span>
        </div>
        <div>
          <small className="text-secondary d-block">Saldo</small>
          <Badge variant={Number(item.saldo) < 0 ? 'danger' : 'success'}>
            {formatarMoeda(item.saldo)}
          </Badge>
        </div>
        <div>
          <small className="text-secondary d-block">Ultima Conf.</small>
          <span>{formatarDataHora(item.data)}</span>
        </div>
      </div>
      {podeConferir && (
        <div className="text-center mt-2">
          <Button size="sm" variant="outline-success" block onClick={() => onConferir(item)}>
            <FaCheck /> Conferir
          </Button>
        </div>
      )}
    </div>
  </div>
);

export default function TabelaSaldos({ saldos, podeConferir, onConferir, linhaSelecionada }) {
  const [ordenacao, setOrdenacao] = useState({ coluna: null, direcao: 'asc' });
  const [colunaHover, setColunaHover] = useState(null);

  const handleOrdenacao = (coluna) => {
    setOrdenacao((prev) => ({
      coluna,
      direcao: prev.coluna === coluna && prev.direcao === 'asc' ? 'desc' : 'asc',
    }));
  };

  const getIconeOrdenacao = (coluna) => {
    if (ordenacao.coluna !== coluna) return null;
    return ordenacao.direcao === 'asc' ? <FaAngleUp /> : <FaAngleDown />;
  };

  const dadosOrdenados = useMemo(() => {
    if (!saldos || !ordenacao.coluna) return saldos || [];
    return [...saldos].sort((a, b) => {
      const valorA = a[ordenacao.coluna];
      const valorB = b[ordenacao.coluna];
      if (valorA == null && valorB == null) return 0;
      if (valorA == null) return ordenacao.direcao === 'asc' ? 1 : -1;
      if (valorB == null) return ordenacao.direcao === 'asc' ? -1 : 1;
      if (typeof valorA === 'string') {
        const comparacao = valorA.localeCompare(valorB, 'pt-BR');
        return ordenacao.direcao === 'asc' ? comparacao : -comparacao;
      }
      return ordenacao.direcao === 'asc' ? valorA - valorB : valorB - valorA;
    });
  }, [saldos, ordenacao]);

  const isSelecionada = (item) =>
    linhaSelecionada &&
    linhaSelecionada.codigo_conta === item.codigo_conta &&
    linhaSelecionada.ge === item.ge &&
    linhaSelecionada.codigo_ativo === item.codigo_ativo;

  return (
    <Card className="shadow-sm">
      <Card.Header className="text-center" style={{ backgroundColor: '#0747a6', color: '#fff' }}>
        <strong>Saldos</strong>
        <span className="ml-2" style={{ fontSize: '0.85rem', opacity: 0.8 }}>
          ({saldos.length} {saldos.length === 1 ? 'registro' : 'registros'})
        </span>
      </Card.Header>
      <Card.Body className="p-0 p-md-3">
        <style>
          {`
            .tabela-saldos {
              margin: 0;
            }
            .tabela-saldos thead th {
              background-color: #0747a6;
              color: #fff;
              border-color: #0651b8;
              cursor: pointer;
              user-select: none;
              transition: background-color 0.2s;
              position: sticky;
              top: 0;
              z-index: 2;
              white-space: nowrap;
              padding: 0.6rem 0.75rem;
              vertical-align: middle;
            }
            .tabela-saldos thead th:hover {
              background-color: #053580;
            }
            .tabela-saldos tbody td {
              vertical-align: middle;
              padding: 0.5rem 0.75rem;
            }
            .tabela-saldos tbody tr:hover {
              background-color: #e8f4ff !important;
            }
            .tabela-saldos tbody tr.table-success {
              background-color: #d4edda !important;
            }
            .tabela-saldos tbody tr.table-success:hover {
              background-color: #c3e6cb !important;
            }
            .tabela-saldos .badge {
              font-size: 0.85rem;
              padding: 0.35em 0.6em;
            }
            .saldos-mobile { display: none; }
            @media (max-width: 768px) {
              .saldos-desktop { display: none !important; }
              .saldos-mobile { display: block; padding: 0.5rem; }
            }
            @media (min-width: 769px) {
              .saldos-desktop {
                border-radius: 8px;
                border: 1px solid #dee2e6;
                max-height: 500px;
                overflow-y: auto;
                overflow-x: auto;
              }
            }
          `}
        </style>

        <div className="saldos-desktop">
          <Table bordered hover size="sm" className="tabela-saldos">
            <thead>
              <tr>
                {colunas.map((col) => (
                  <th
                    key={col.chave}
                    style={{ textAlign: col.align, width: col.width || 'auto' }}
                    onClick={() => handleOrdenacao(col.chave)}
                    onMouseEnter={() => setColunaHover(col.chave)}
                    onMouseLeave={() => setColunaHover(null)}
                  >
                    {col.label}
                    <span style={{ marginLeft: '4px' }}>
                      {getIconeOrdenacao(col.chave)}
                    </span>
                  </th>
                ))}
                {podeConferir && (
                  <th style={{ textAlign: 'center', width: '100px' }}>Acao</th>
                )}
              </tr>
            </thead>
            <tbody>
              {dadosOrdenados.length === 0 ? (
                <tr>
                  <td colSpan={podeConferir ? 6 : 5} className="text-center text-muted py-4">
                    Nao ha saldos a serem listados.
                  </td>
                </tr>
              ) : dadosOrdenados.map((item, idx) => (
                <tr key={idx} className={isSelecionada(item) ? 'table-success' : ''}>
                  <td style={{ textAlign: 'center' }}>{item.ge}</td>
                  <td style={{ textAlign: 'center' }}>{item.codigo_conta}</td>
                  <td style={{ textAlign: 'center' }}>{item.nome}</td>
                  <td style={{ textAlign: 'center' }}>
                    <Badge variant={Number(item.saldo) < 0 ? 'danger' : 'success'}>
                      {formatarMoeda(item.saldo)}
                    </Badge>
                  </td>
                  <td style={{ textAlign: 'center' }}>
                    {formatarDataHora(item.data)}
                  </td>
                  {podeConferir && (
                    <td style={{ textAlign: 'center' }}>
                      <Button size="sm" variant="outline-success" onClick={() => onConferir(item)}>
                        <FaCheck /> Conferir
                      </Button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </Table>
        </div>

        <div className="saldos-mobile">
          {dadosOrdenados.map((item, idx) => (
            <MobileCard
              key={idx}
              item={item}
              podeConferir={podeConferir}
              onConferir={onConferir}
              isSelecionada={isSelecionada(item)}
            />
          ))}
        </div>
      </Card.Body>
    </Card>
  );
}
